import { IEthnicity, ILanguage, ITaxonomy } from './search.interface';

// Interface for doctors profile data.

// Hey you awesome! Everytime you change this interface, update the version in the in ZkuService!
interface IProfessionalProfileBase {
	acceptsHalfHour: boolean;
	acceptsSubscriptions: boolean;
	active: boolean;
	address?: string;
	addressComplement?: string;
	addressCountry?: string;
	addressNumber?: string;
	bankAccount?: string;
	bankAgency?: string;
	bankName?: string;
	birthdate?: string;
	blockNewClients: boolean;
	businessFiscalId?: string;
	canAcceptTags: ITaxonomy['id'][];
	city?: string;
	country?: string;
	currency?: string;
	description?: string;
	displayName: string;
	education?: IProfessionalEducation[];
	email: string;
	experience?: string;
	fiscalId?: string;
	gender?: 'M' | 'F';
	id: string;
	identityCard?: string;
	imageUrl?: string;
	isOnline: boolean;
	isVisible: boolean;
	legalPerson?: IProfessionalLegalPerson;
	isCompleteOnboard?: boolean;
	license?: string;
	nutritionistLicense?: IProfessionalNutritionistLicense;
	linkedInUrl?: string | null;
	location?: { country: string; region?: string };
	mainType: string;
	marginInMinutesToReschedule: number;
	marginInMinutesToSchedule: number;
	mobileNumber?: string;
	naturalPerson?: IProfessionalNaturalPerson;
	neighborhood?: string;
	offersFirstSession: boolean;
	originalSignatureUrl?: string;
	paymentAccount?: IProfessionalPaymentAccount;
	phoneNumber?: string;
	postalCode?: string;
	priceHalfHour?: number;
	priceOneHour?: number;
	profileCompletion?: ProfileCompletion;
	profileUrl?: string;
	rating: any;
	requestedMembershipCancelationAt?: Date | null;
	signatureUrl?: string;
	slug?: string;
	socialReason?: string;
	state?: string;
	stripeConnectedAccount?: string | null;
	stripeDetailsSubmitted?: boolean | null;
	stripePaymentOk?: boolean | null;
	summary?: string;
	timezone?: string;
	trainee: boolean;
	updatedAt: Date;
	userName?: string;
	validationLastCompletedAt?: Date | null;
	validationLastRequestedAt?: Date | null;
	validationStatus: IProfessionalBankValidationStatus;
	videoUrl?: string | null;
	paymentMetadata?: IProfessionalPaymentMedata;
	acceptOnlyWholeHour?: boolean;
}

export interface IProfessionalPaymentMedata {
	customerId: string;
	country: string;
}

export interface IProfessionalNutritionistLicense {
	license?: string;
	region?: string;
}

export interface IProfessionalPaymentAccount {
	ispb: string;
	bankName: string;
	bankCode: string;
	bankFullName: string;
	agency: string;
	account: string;
	accountDigit: string;
	dontAcceptPix: boolean;
	pixKey?: string;
	pixType?: string;
	updatedAt?: Date | null;
	createdAt?: Date | null;
	validationStatus: IProfessionalBankValidationStatus;
	validationLastRequestedAt?: Date | null;
	validationLastCompletedAt?: Date | null;
	status?: string;
}

export interface ProfileCompletion {
	activateProfile: boolean;
	completedActivateProfileAt?: Date;
	performSessions: boolean;
	completedPerformSessionsAt?: Date;
	appearOnSearch: boolean;
	completedAppearOnSearchAt?: Date;
	issueReceipts: boolean;
	completedIssueReceiptsAt?: Date;
}

export interface IZkuProfessional extends IProfessionalProfileBase {
	age?: string;
	specialistIsCoe?: boolean;
	createdAt?: Date;
	especialty?: string;
	image?: string;
	isPremium?: boolean;
	name?: string;
	profile?: string;
	tags?: ITaxonomy[];
}
export interface IProfessionalProfile extends IProfessionalProfileBase {
	createdAt?: Date;
	ethnicity?: IEthnicity;
	requestedMembershipCancelationAt?: Date | null;
	spokenLanguages?: ILanguage[];
	tags?: ITaxonomy[];
}

export interface IProfessionalProfileUpdate extends IProfessionalProfileBase {
	spokenLanguages?: ILanguage['id'][];
	ethnicity?: IEthnicity['id'];
	tags: ITaxonomy['id'][];
}

export interface IProfessionalLegalPerson {
	address: string;
	addressComplement: string;
	addressNumber: string;
	city: string;
	neighborhood: string;
	postalCode: string;
	state: string;
	pis: string;
	serviceCode: string[];
	documents?: IProfessionalBillingDocument[];
}

export interface IProfessionalBillingDocument {
	documentType: EBillingFileType;
	url: string;
}

export interface IProfessionalNaturalPerson {
	nationalIdentityCard?: string;
	pis: string;
	rne?: string;
	ccm?: string;
	serviceCode: string[];
	isForeign?: boolean;
	liveOutsideBrazil?: boolean;
	documents?: IProfessionalBillingDocument[];
}

export enum EBillingFileType {
	ID = 'IDENTIDADE',
	Address = 'COMPROVANTE_ENDERECO',
	PIS = 'PIS',
	WorkCard = 'CARTEIRA_TRABALHO',
	CNPJ_Card = 'CARTAO_CNPJ',
}

export enum EBillingType {
	Natural = 'natural',
	Legal = 'legal',
}

export interface IBillingFile {
	documentType: EBillingFileType;
	personType: EBillingType;
	filesInBase64: string[];
}

export interface UploadPhotoResponse {
	uploadUrl: string;
	imageUrl: string;
}

export interface UploadSignatureResponse {
	uploadUrl: string;
	signatureUrl: string;
}

export interface RequestActivationResponse {
	requestedActivation: boolean;
}

export interface ProfileCompletionItem {
	link?: string;
	text: string;
	completed: boolean;
}

export interface ProfileCompletionResponse {
	steps: {
		activateProfile: {
			todo: ProfileCompletionItem[];
		};
		performSessions: {
			todo: ProfileCompletionItem[];
		};
		appearOnSearch: {
			todo: ProfileCompletionItem[];
		};
		issueReceipts: {
			todo: ProfileCompletionItem[];
		};
		boostProfile: {
			todo: ProfileCompletionItem[];
		};
	};
}

export interface IProfessionalEducation {
	id?: number;
	professionalId?: string;
	educationType?: IProfessionalEducationType | null;
	title?: string;
	institution?: string;
	educationYear?: number;
}

export type IProfessionalEducationType =
	| 'degree'
	| 'master'
	| 'doctorate'
	| 'phd-mba'
	| 'course'
	| 'other';

export interface ProfileSubscriptionAndContactTiers {
	hasActiveSubscription: boolean;
	contactTiers: string[];
}

export interface ProfileSessionPriceUpdate {
	canUpdateOneHour: boolean;
	canUpdateHalfHour: boolean;
	lastSessionOneHourPriceUpdatedAt: Date;
	lastSessionHalfHourPriceUpdatedAt: Date;
}

export enum EProfileBillingStatus {
	Blank = '',
	InProgress = 'in_progress',
	RequestingChange = 'requesting_change',
	Inactive = 'cancelation_accepted',
	Active = 'active',
	ChangeRequested = 'change_requested',
	ChangeAccepted = 'change_accepted',
	WaitingApproval = 'waiting_approval',
	WaitingChange = 'waiting_change',
	UpdateRequested = 'update_requested',
}

export interface IProfessionalPerson {
	type: EBillingType | null;
	status: EProfileBillingStatus;
	canUpdatedPaymentAccount?: boolean;
	lastNaturalPersonPeriod?: {
		startsAt: string;
		endsAt: string;
	};
	lastLegalPersonPeriod?: {
		startsAt: string;
		endsAt: string;
	};
	idWallReportStatus?: string;
	updatePaymentAccountAt?: string;
	lastProfessionalPersonEndsAt?: string;
	errorCode?: number;
	professionalData?: IProfessionalPersonData;
	blockEditPaymentAccount?: {
		block: boolean;
		dayStartAllowEdit: number;
		dayEndAllowEdit: number;
	};
}

export interface IProfessionalPersonData {
	postalCode?: string;
	address?: string;
	addressNumber?: string;
	addressComplement?: string;
	neighborhood?: string;
	city?: string;
	state?: string;
	addressCountry?: string;
	businessFiscalId?: string;
	fiscalId: string;
	naturalPerson?: IProfessionalNaturalPerson;
	legalPerson?: IProfessionalLegalPerson;
	socialReason?: string;
}

export interface ICNPJValidation {
	businessFiscalId: string;
	fiscalId: string;
}

export interface IProfessionalFinancial {
	professionalId: string;
	timestamp?: Date;
	status: string;
	protocol: string;
	errorCode: number;
	observation?: string;
}

export enum IProfessionalBankValidationStatus {
	pending = 'pending',
	validated = 'validated',
	has_errors = 'has_errors',
	on_going = 'on_going',
}
